import type { BalancePostingWrapper } from '@festina/life-general-types/life'
import { notEmpty, typeUtils } from '@festina-life/core'
import { useEffect, useState } from 'react'

import { useHook } from '../../lenteHooks'

type FetchedBalancePostingWrappers = {
  postingWrappers?: BalancePostingWrapper[]
  isLoading: boolean
  error: Error | null
}

export const useLazyFetchLifeBalancePostingsForWorkPadIds = (
  workPadIds: string[],
): FetchedBalancePostingWrappers => {
  const { tenantId } = useHook('routing.general.useEntityParams')()
  const { mutateAsync: fetchBalancePostings } = useHook(
    'lazyFetchers.life.useLazyFetchBalancePostings',
  )()
  const [postingWrappers, setPostingWrappers] = useState<
    BalancePostingWrapper[] | 'loading' | Error
  >('loading')

  useEffect(() => {
    const fetchData = async () => {
      try {
        const balancePostingWrapperPromises = workPadIds.map((workPadId) =>
          fetchBalancePostings({ tenantId, workPadId }),
        )

        const balancePostingCollections = await Promise.all(
          balancePostingWrapperPromises,
        )

        const balancePostingWrappers = balancePostingCollections
          ?.flatMap((collection) => collection?.balancePostingWrappers ?? [])
          .filter(notEmpty)

        setPostingWrappers(balancePostingWrappers)
      } catch (error) {
        setPostingWrappers(error)
      }
    }

    void fetchData()
  }, [fetchBalancePostings, tenantId, workPadIds])

  if (typeUtils.isLoading(postingWrappers)) {
    return {
      isLoading: true,
      error: null,
    }
  }

  if (typeUtils.isError(postingWrappers)) {
    return {
      isLoading: false,
      error: postingWrappers,
    }
  }

  return {
    postingWrappers,
    isLoading: false,
    error: null,
  }
}
